import { AfterViewInit, Component, OnInit, OnDestroy } from '@angular/core';
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { GlobalSpinnerService } from 'src/app/core/services/loader/global-spinner.service';
import { PasswordConfirmationValidatorService } from 'src/app/core/custom-validators/password-confirmation-validator.service';
import { UserCompany } from '../../models/user-company';
import { AuthService } from '../../services/auth/auth.service';
import { SubscriptionService } from 'src/app/modules/feature-toggle/services/subscription.service';
import { ThemeService } from 'src/app/core/services/theme/theme.service';
import { GDPR_CONSTANTS } from 'src/app/core/constants/gdpr.constants';
import { AuthLegalFooterComponent } from '../../components/auth-legal-footer/auth-legal-footer.component';
import { environment } from 'src/environments/environment';
import { XdsButtonComponent, XdsIconButtonComponent, XdsFormFieldComponent, XdsInputComponent, XdsCheckboxComponent } from '../../../../shared/ui';
import { ClientLoggerService } from 'src/app/core/services/logging/client-logger.service';
import { LogCategory } from 'src/app/core/services/logging/client-log.models';
import { GoogleAdsConversionService } from 'src/app/core/services/google-ads/google-ads-conversion.service';

/** Minimaler Typ für die global geladene Cloudflare-Turnstile-API. */
interface TurnstileApi {
  render(
    container: string | HTMLElement,
    options: Record<string, unknown>
  ): string;
  remove(widgetId: string): void;
  reset(widgetId?: string): void;
}

@Component({
    selector: 'app-register-user',
    templateUrl: './register-user.component.html',
    styleUrls: ['./register-user.component.css'],
    imports: [CommonModule, ReactiveFormsModule, RouterModule, TranslateModule, AuthLegalFooterComponent, XdsButtonComponent, XdsIconButtonComponent, XdsFormFieldComponent, XdsInputComponent, XdsCheckboxComponent]
})
export class RegisterUserComponent implements OnInit, AfterViewInit, OnDestroy {
  registerForm: UntypedFormGroup;
  showPassword = false;
  showConfirmPassword = false;
  // Cloudflare Turnstile (einwilligungsfreies Captcha statt Google reCAPTCHA)
  private turnstileScript?: HTMLScriptElement;
  private turnstileWidgetId: string | null = null;
  private turnstileRenderAttempts = 0;
  turnstileToken: string | null = null;
  passwordStrength = 0; // NEU: Für die Passwort-Stärke-Anzeige
  // True, sobald „Kostenloses Testkonto erstellen" einmal (erfolglos) geklickt wurde.
  // Steuert sichtbares Feedback (Sammelhinweis am Button), das erst NACH dem ersten
  // Absende-Versuch erscheint – nicht schon beim Laden.
  submitAttempted = false;
  // Standard-Testphase in Tagen (SSOT: Backend). 14 = sinnvoller Fallback,
  // wird in loadAvailableSubscriptions() aus der API überschrieben.
  trialDays = 14;
  /**
   * FE-12 P5: gesetzt, wenn der Auto-Login nach der Registrierung kein Token brachte (unbestätigte
   * oder schon bekannte Adresse, falsches Passwort, Firmenauswahl, Netz). Die Seite zeigt dann statt
   * des Formulars „Prüfe dein Postfach“ — für neue und bekannte Adressen identisch.
   */
  checkInboxEmail: string | null = null;
  private funnelContext: Record<string, string> = {};
  private emittedFunnelStages = new Set<string>();
  private formInteractionTracked = false;
  private submitAttemptNumber = 0;
  // Zeitpunkt, ab dem der Nutzer auf dieser Seite ist — Basis für die Verweildauer im
  // Abbruch-Signal. Wird nur EINMAL gesetzt, weil `initializeComponent()` zweimal laufen kann
  // (ngOnInit + ionViewWillEnter) und die Uhr sonst beim zweiten Lauf neu anfinge.
  private pageEnteredAt = 0;
  private submitStarted = false;
  private abandonmentReported = false;
  private formRenderedTracked = false;
  private pageHideHandler?: () => void;
  private visibilityHandler?: () => void;

  constructor(
    private authService: AuthService,
    private passConfValidator: PasswordConfirmationValidatorService,
    private router: Router,
    private spinner: GlobalSpinnerService,
    private subscriptionService: SubscriptionService,
    private route: ActivatedRoute,
    public themeService: ThemeService,
    private logger: ClientLoggerService,
    private googleAdsConversion: GoogleAdsConversionService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.initializeComponent();
  }

  ngAfterViewInit(): void {
    this.trackFormRenderedAfterPaint();
  }

  // Ionic Lifecycle Hook - wird bei jeder Navigation zur Seite aufgerufen
  ionViewWillEnter(): void {
    this.initializeComponent();
  }

  private initializeComponent(): void {
    this.loadTurnstileScript();

    this.registerForm = new UntypedFormGroup({
      firstName: new UntypedFormControl('', Validators.required),
      lastName: new UntypedFormControl('', Validators.required),
      email: new UntypedFormControl('', [
        Validators.required,
        Validators.email,
      ]),
      password: new UntypedFormControl('', [
        Validators.required,
        Validators.minLength(12),
        this.validatePasswordStrength.bind(this),
      ]),
      companyName: new UntypedFormControl('', Validators.required),
      confirmPassword: new UntypedFormControl('', Validators.required),
      // DSGVO: getrennte Pflicht-Bestätigungen – AGB = Akzeptanz, Datenschutz = Kenntnisnahme
      termsAccepted: new UntypedFormControl(false, Validators.requiredTrue),
      privacyPolicyAccepted: new UntypedFormControl(false, Validators.requiredTrue),
    });

    this.registerForm
      .get('confirmPassword')
      ?.setValidators([
        Validators.required,
        this.passConfValidator.validateConfirmPassword(
          this.registerForm.get('password')!
        ),
      ]);

    // Passwort-Stärke bei Änderungen berechnen
    this.registerForm.get('password')?.valueChanges.subscribe((value) => {
      this.passwordStrength = this.calculatePasswordStrength(value);
    });

    // Vollzugriff-Testphase: KEIN Tarifschritt mehr in der Registrierung. Es wird kein
    // kostenpflichtiger Tarif gewählt; der Berechtigungsplan wird serverseitig bestimmt.
    // Nur die dynamische Testphasen-Länge (SSOT: Backend) wird geladen.
    this.loadTrialPeriod();

    // Capture landing page tracking parameters for end-to-end journey tracking.
    // fp_journey ist eine zufaellige Einmal-ID ohne Personen-/Formulardaten.
    //
    // ⚠️ Jeder Wert läuft durch `singleParam()`. Angular liefert bei einem DOPPELTEN
    // Query-Parameter ein Array, und dieses Array landete unverändert im Log — die Property
    // `Gclid` stand am 2026-08-20/21 als `["CjwK…","CjwK…"]` in Seq und ging so an Google,
    // das sie mit „The imported gclid could not be decoded" ablehnte. Die Ursache (eine
    // Landingpage-Directive, die den Parameter ein zweites Mal anhängte) ist behoben; diese
    // Prüfung bleibt, weil sie den Fehler unabhängig von der Quelle abfängt.
    const qp = this.route.snapshot.queryParams;
    const p = (key: string): string => this.singleParam(qp[key]);
    let previousRaw: Record<string, unknown> = {};
    try {
      previousRaw = JSON.parse(sessionStorage.getItem('lp_tracking') || '{}');
    } catch {
      previousRaw = {};
    }
    // Auch der Bestand im sessionStorage kann noch Arrays aus früheren Sitzungen enthalten.
    const prev = (key: string): string => this.singleParam(previousRaw[key]);

    const journeyId = p('fp_journey') || p('trackingId') || prev('journeyId') || prev('trackingId') || this.createJourneyId();
    const lpTracking = {
      journeyId,
      trackingId: p('trackingId') || prev('trackingId') || journeyId,
      ref: p('ref') || prev('ref') || 'direct',
      cta: p('cta') || prev('cta') || '',
      score: p('score') || prev('score') || '',
      level: p('level') || prev('level') || '',
      scroll: p('scroll') || prev('scroll') || '',
      sec: p('sec') || prev('sec') || '',
      comp: p('comp') || prev('comp') || '',
      dur: p('dur') || prev('dur') || '',
      dev: p('dev') || prev('dev') || '',
      pg: p('pg') || prev('pg') || '',
      fun: p('fun') || prev('fun') || '',
      pat: p('pat') || prev('pat') || '',
      utm_source: p('utm_source') || prev('utm_source') || '',
      utm_medium: p('utm_medium') || prev('utm_medium') || '',
      utm_campaign: p('utm_campaign') || prev('utm_campaign') || '',
      gclid: p('gclid') || prev('gclid') || '',
      // Unverbindliche Analyse-Info aus der Pricing-Seite. KEINE Tarifwahl, KEINE Trial-Berechtigung.
      preferredPlanId: p('preferredPlanId') || p('plan') || prev('preferredPlanId') || ''
    };
    this.funnelContext = lpTracking;
    sessionStorage.setItem('lp_tracking', JSON.stringify(lpTracking));
    this.googleAdsConversion.initialize(this.googleAdsConversion.hasTransferredMarketingConsent());

    // `route_entered` — NICHT `form_view`. Der Schritt sagt aus, dass Angular die Route betreten
    // hat; er sagt NICHTS darüber, ob der Nutzer je ein Formular gesehen hat. Der frühere Name
    // behauptete das Gegenteil, und die Auswertung hat es geglaubt: Am 22.08.2026 wurde er um
    // 18:15:22 gemeldet, während die Übersetzungsdatei `en.json` erst um 18:15:25 eintraf — die
    // Marketing-Auswertung wies den Besuch trotzdem als „Registrierung angesehen" aus, obwohl der
    // Besucher zu diesem Zeitpunkt noch auf eine unfertige Seite sah.
    // Der tatsächlich sichtbare Zustand wird getrennt als `form_rendered` gemeldet.
    if (this.pageEnteredAt === 0) {
      this.pageEnteredAt = Date.now();
    }
    this.trackFunnelStep('route_entered');
    this.registerLeaveListeners();

    // Eine Conversion für „Registrierung gestartet“ darf erst nach einer tatsächlichen
    // Texteingabe entstehen. Checkboxen, Fokus und programmatisches patchValue() sind
    // dafür kein Beleg. Es werden nur Feldname und Anzahl, niemals Inhalte, protokolliert.
    for (const name of ['firstName', 'lastName', 'companyName', 'email', 'password', 'confirmPassword']) {
      const control = this.registerForm.get(name);
      control?.valueChanges.subscribe((value) => {
        if (this.formInteractionTracked || !control.dirty || !String(value ?? '').trim()) return;
        this.formInteractionTracked = true;
        this.trackFunnelStep('form_interaction', { InputField: name, FilledFields: 1 });
      });
    }
  }

  /**
   * Meldet `form_rendered`, sobald das Formular wirklich auf dem Bildschirm steht — zwei
   * Frames nach `ngAfterViewInit`, also nach dem ersten Paint.
   *
   * `LabelsTranslated` ist der eigentliche Grund für diesen Schritt. `main.ts` lädt die
   * Übersetzungen blockierend mit einem Timeout von 2000 ms und fällt danach auf Englisch bzw.
   * auf gar keine Übersetzung zurück. Auf Mobilfunk reicht das für ~228 KB `de.json` nicht immer.
   * Wer dann ein Formular mit rohen Schlüsseln (`REG_CTA_SUBMIT`) sieht, hält die Seite für
   * kaputt und geht — bisher war dieser Zustand in keinem Log von einem normalen Abbruch zu
   * unterscheiden.
   */
  private trackFormRenderedAfterPaint(): void {
    if (this.formRenderedTracked) {
      return;
    }
    this.formRenderedTracked = true;

    const report = () => {
      const probeKey = 'REG_CTA_SUBMIT';
      const translated = this.translate.instant(probeKey);
      this.trackFunnelStep('form_rendered', {
        MsSinceRouteEntered: this.msOnPage(),
        LabelsTranslated: translated !== probeKey && !!translated,
        ActiveLang: this.translate.currentLang || '',
        CaptchaReady: this.captchaSolved,
        Viewport: `${window.innerWidth}x${window.innerHeight}`,
      });
    };

    if (typeof requestAnimationFrame === 'function') {
      // Zwei Frames: der erste läuft noch vor dem Paint, der zweite danach.
      requestAnimationFrame(() => requestAnimationFrame(report));
    } else {
      setTimeout(report, 0);
    }
  }

  /**
   * Hängt das Abbruch-Signal an `pagehide` und `visibilitychange`.
   *
   * Bewusst NICHT an `beforeunload`: Das feuert auf iOS Safari häufig nicht, und genau die
   * mobilen Besucher aus den Suchanzeigen sind die Gruppe, die hier vermessen werden soll.
   */
  private registerLeaveListeners(): void {
    if (this.pageHideHandler) {
      return; // `initializeComponent()` kann zweimal laufen — Listener nur einmal binden.
    }

    this.pageHideHandler = () => this.reportAbandonment('pagehide');
    this.visibilityHandler = () => {
      if (document.visibilityState === 'hidden') {
        this.reportAbandonment('hidden');
      }
    };

    window.addEventListener('pagehide', this.pageHideHandler);
    document.addEventListener('visibilitychange', this.visibilityHandler);
  }

  /**
   * Meldet, mit welchem Zustand der Nutzer die Seite verlassen hat.
   *
   * Nur einmal je Besuch: `visibilitychange` feuert auch beim kurzen App-Wechsel, und ein
   * Rückkehrer würde sonst mehrfach als Abbruch gezählt. Kommt er zurück und sendet ab, ist das
   * über `submit_started` desselben `JourneyId` erkennbar — die Auswertung kann beides verbinden.
   *
   * `FilledFields` ist eine Zahl, kein Inhalt: Es geht darum, WIE WEIT jemand kam, nicht WAS er
   * geschrieben hat. Eingaben aus dem Registrierungsformular dürfen nicht ins Log.
   */
  private reportAbandonment(reason: 'pagehide' | 'hidden' | 'navigated'): void {
    if (this.submitStarted || this.abandonmentReported) {
      return;
    }
    this.abandonmentReported = true;

    const controls = Object.keys(this.registerForm?.controls ?? {});
    const filled = controls.filter((name) => {
      const value = this.registerForm.get(name)?.value;
      return value !== '' && value !== false && value !== null && value !== undefined;
    });

    this.trackFunnelStep(
      'form_abandoned',
      {
        Reason: reason,
        MsOnPage: this.msOnPage(),
        FilledFields: filled.length,
        TotalFields: controls.length,
        TouchedForm: this.formInteractionTracked,
        SubmitAttempts: this.submitAttemptNumber,
        CaptchaSolved: this.captchaSolved,
      },
      false
    );

    // sendBeacon statt fetch: Beim Verlassen der Seite darf der Browser einen fetch verwerfen.
    this.logger.forceFlushSync();
  }

  /** Verweildauer auf der Seite in Millisekunden (0, falls die Uhr nie startete). */
  private msOnPage(): number {
    return this.pageEnteredAt === 0 ? 0 : Date.now() - this.pageEnteredAt;
  }

  ngOnDestroy(): void {
    // Verlässt der Nutzer die Seite per In-App-Navigation (zurück zum Login), feuert weder
    // `pagehide` noch `visibilitychange` — der Abbruch würde sonst unbemerkt bleiben.
    this.reportAbandonment('navigated');

    if (this.pageHideHandler) {
      window.removeEventListener('pagehide', this.pageHideHandler);
      this.pageHideHandler = undefined;
    }
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = undefined;
    }

    // Turnstile-Widget entfernen (falls gerendert)
    const turnstile = (window as unknown as { turnstile?: TurnstileApi })
      .turnstile;
    if (turnstile && this.turnstileWidgetId !== null) {
      try {
        turnstile.remove(this.turnstileWidgetId);
      } catch {
        // Widget evtl. schon entfernt -> ignorieren
      }
    }
    this.turnstileWidgetId = null;
    this.turnstileToken = null;

    // Script-Tag aus dem Head entfernen
    if (this.turnstileScript && document.head.contains(this.turnstileScript)) {
      document.head.removeChild(this.turnstileScript);
    }
  }

  loadTurnstileScript(): void {
    // Ohne konfigurierten Site-Key (z. B. Prod vor Go-Live) Captcha überspringen.
    if (!environment.turnstileSiteKey) {
      return;
    }

    const turnstile = (window as unknown as { turnstile?: TurnstileApi })
      .turnstile;
    if (turnstile) {
      // Script bereits geladen (z. B. Rück-Navigation) -> direkt rendern.
      this.renderTurnstileWhenReady();
      return;
    }

    if (
      !document.querySelector(
        'script[src^="https://challenges.cloudflare.com/turnstile/v0/api.js"]'
      )
    ) {
      this.turnstileScript = document.createElement('script');
      this.turnstileScript.src =
        'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      this.turnstileScript.async = true;
      this.turnstileScript.defer = true;
      document.head.appendChild(this.turnstileScript);
    }

    this.renderTurnstileWhenReady();
  }

  private renderTurnstileWhenReady(): void {
    if (this.turnstileWidgetId !== null) {
      return; // bereits gerendert
    }

    const turnstile = (window as unknown as { turnstile?: TurnstileApi })
      .turnstile;
    const container = document.getElementById('turnstile-container');

    if (turnstile && container) {
      this.turnstileWidgetId = turnstile.render(container, {
        sitekey: environment.turnstileSiteKey,
        // Unsichtbar, solange kein echter Challenge nötig ist (dezent wie das alte reCAPTCHA-Badge).
        appearance: 'interaction-only',
        theme: 'auto',
        callback: (token: string) => {
          this.turnstileToken = token;
        },
        'expired-callback': () => {
          this.turnstileToken = null;
        },
        'error-callback': () => {
          this.turnstileToken = null;
        },
      });
      return;
    }

    // Script/Container noch nicht bereit -> begrenzt erneut versuchen (max. ~5s).
    if (this.turnstileRenderAttempts < 50) {
      this.turnstileRenderAttempts++;
      setTimeout(() => this.renderTurnstileWhenReady(), 100);
    }
  }

  loadTrialPeriod(): void {
    // Testphasen-Länge dynamisch aus der anonymen Pricing-API (SSOT: Backend).
    // Bei Fehler/leer bleibt der Fallback (14 Tage) erhalten.
    this.subscriptionService.loadDefaultTrialPeriodDays().subscribe({
      next: (days) => {
        if (days && days > 0) {
          this.trialDays = days;
        }
      },
      error: () => {
        // Fallback (this.trialDays = 14) beibehalten
      },
    });
  }

  toggleShowPassword(): void {
    this.showPassword = !this.showPassword;
  }

  toggleShowConfirmPassword(): void {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  // Passwort-Stärke berechnen (0-5)
  calculatePasswordStrength(password: string): number {
    if (!password) return 0;

    let strength = 0;

    // Länge >= 12
    if (password.length >= 12) strength++;

    // Enthält Großbuchstaben
    if (/[A-Z]/.test(password)) strength++;

    // Enthält Kleinbuchstaben
    if (/[a-z]/.test(password)) strength++;

    // Enthält Ziffern
    if (/[0-9]/.test(password)) strength++;

    // Enthält Sonderzeichen — deckungsgleich mit Backend Identity
    // RequireNonAlphanumeric (Startup.cs): JEDES Nicht-alphanumerische Zeichen zählt.
    if (/[^A-Za-z0-9]/.test(password)) strength++;

    return strength;
  }

  // Passwort-Stärke-Label (returns translation key, template applies | translate)
  getPasswordStrengthLabel(): string {
    switch (this.passwordStrength) {
      case 0:
      case 1:
        return 'REG_PW_LEVEL_WEAK';
      case 2:
        return 'REG_PW_LEVEL_MEDIUM';
      case 3:
        return 'REG_PW_LEVEL_GOOD';
      case 4:
        return 'REG_PW_LEVEL_STRONG';
      case 5:
        return 'REG_PW_LEVEL_VERY_STRONG';
      default:
        return 'REG_PW_LEVEL_WEAK';
    }
  }

  // Returns "weak" | "full" | null for [attr.data-strength] on .pw-bars
  getPasswordStrengthData(): 'weak' | 'full' | null {
    if (this.passwordStrength <= 1) return 'weak';
    if (this.passwordStrength === 5) return 'full';
    return null;
  }

  // Theme toggle wrapper
  toggleTheme(): void {
    this.themeService.toggleTheme();
  }

  // Returns Font-Awesome icon class (auth-page uses fa-light, ThemeService returns Material Icon names)
  getThemeIconClass(): string {
    const mode = this.themeService.getThemeIcon();
    if (mode === 'brightness_auto') return 'fa-circle-half-stroke';
    if (mode === 'light_mode') return 'fa-sun';
    return 'fa-moon';
  }

  validatePasswordStrength(
    control: UntypedFormControl
  ): { [key: string]: boolean } | null {
    const value = control.value || '';
    const errors: { [key: string]: boolean } = {};

    if (value.length < 12) {
      errors['minlength'] = true;
    }
    if (!/[A-Z]/.test(value)) {
      errors['missingUpper'] = true;
    }
    if (!/[a-z]/.test(value)) {
      errors['missingLower'] = true;
    }
    if (!/[0-9]/.test(value)) {
      errors['missingDigit'] = true;
    }
    // Backend akzeptiert JEDES Nicht-alphanumerische Zeichen (RequireNonAlphanumeric).
    // Das FE muss dasselbe Set prüfen, sonst werden gültige Passwörter (z. B. mit
    // _ + = ' oder Leerzeichen) fälschlich als „Sonderzeichen fehlt" abgelehnt.
    if (!/[^A-Za-z0-9]/.test(value)) {
      errors['missingSpecial'] = true;
    }

    return Object.keys(errors).length ? errors : null;
  }

  validateControl(controlName: string): boolean {
    const control = this.registerForm.get(controlName);
    return control ? control.invalid && control.touched : false;
  }

  hasError(controlName: string, errorName: string): boolean {
    return this.registerForm.get(controlName)?.hasError(errorName) || false;
  }

  /**
   * Scrollt zum ersten unvollständigen Element (in visueller Reihenfolge) und
   * fokussiert es. Vermeidet, dass der Nutzer auf der langen Seite raten muss,
   * warum „Konto eröffnen" nicht durchgeht.
   */
  private scrollToFirstError(): void {
    // Textfelder in visueller Reihenfolge; element-id = `reg-${controlName}`.
    const fieldOrder = [
      'firstName',
      'lastName',
      'companyName',
      'email',
      'password',
      'confirmPassword',
    ];
    for (const name of fieldOrder) {
      if (this.registerForm.get(name)?.invalid) {
        this.focusAndScroll(`reg-${name}`);
        return;
      }
    }

    if (this.registerForm.get('termsAccepted')?.invalid) {
      this.focusAndScroll('reg-terms');
      return;
    }
    if (this.registerForm.get('privacyPolicyAccepted')?.invalid) {
      this.focusAndScroll('reg-privacy');
      return;
    }

    if (!this.captchaSolved) {
      this.focusAndScroll('turnstile-container', false);
    }
  }

  /** Scrollt ein Element mittig in den Viewport und fokussiert es optional. */
  private focusAndScroll(elementId: string, focus = true): void {
    const el = document.getElementById(elementId);
    if (!el) {
      return;
    }
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    if (focus) {
      // Defensive: Trifft die id einen Wrapper (z. B. einen Komponenten-Host statt des
      // nativen Controls), auf das erste fokussierbare Kind ausweichen — ein Custom-Element-
      // Host ist ohne tabindex nicht fokussierbar, `focus()` liefe sonst ins Leere.
      const target: HTMLElement =
        el.matches('input, select, textarea, button, [tabindex]')
          ? el
          : el.querySelector<HTMLElement>(
              'input, select, textarea, button, [tabindex]'
            ) ?? el;
      // Fokus leicht verzögert, damit das Smooth-Scrolling nicht abgebrochen wird.
      setTimeout(() => target.focus({ preventScroll: true }), 150);
    }
  }

  /** True, wenn kein Captcha nötig ist oder bereits ein Turnstile-Token vorliegt. */
  get captchaSolved(): boolean {
    return !environment.turnstileSiteKey || !!this.turnstileToken;
  }

  registerUser(): void {
    this.submitAttemptNumber++;

    // Vollzugriff-Testphase: kein Tarifschritt mehr → keine `subscriptionMissing`-Blockierung.
    // Der Button ist bewusst NICHT disabled: Ein unvollständiges Formular gibt konkretes
    // Feedback statt eines wirkungslosen Klicks. Alle Felder auf „touched" setzen
    // (Inline-Fehler sichtbar), dann zum ersten fehlenden Feld scrollen + fokussieren.
    if (!this.registerForm.valid || !this.captchaSolved) {
      this.submitAttempted = true;
      const invalidFields = Object.keys(this.registerForm.controls)
        .filter((name) => this.registerForm.get(name)?.invalid);
      this.trackFunnelStep('submit_blocked', {
        Attempt: this.submitAttemptNumber,
        InvalidFields: invalidFields.join(','),
        CaptchaMissing: !this.captchaSolved,
        TermsMissing: this.registerForm.get('termsAccepted')?.invalid || false,
        PrivacyMissing: this.registerForm.get('privacyPolicyAccepted')?.invalid || false
      }, false);
      this.registerForm.markAllAsTouched();
      this.scrollToFirstError();
      return;
    }

    this.spinner.show();
    // Ab hier ist der Besuch kein Abbruch mehr: Der anschließende Seitenwechsel nach erfolgreicher
    // Registrierung würde sonst als `form_abandoned` gemeldet und die Abbruchquote verfälschen.
    this.submitStarted = true;
    this.trackFunnelStep('submit_started', { Attempt: this.submitAttemptNumber }, false);
    const formValues = this.registerForm.value;
    const user: UserCompany = {
      id: null,
      FirstName: formValues.firstName,
      LastName: formValues.lastName,
      Email: formValues.email,
      Password: formValues.password,
      CompanyName: formValues.companyName,
      // KEIN SubscriptionPlanId mehr: Die Vollzugriff-Testphase wählt keinen kostenpflichtigen
      // Tarif; der Berechtigungsplan wird serverseitig aufgelöst.
      // Token-Feldname bleibt 'recaptchaToken' (minimaler Backend-Vertrag);
      // Inhalt ist nun ein Cloudflare-Turnstile-Token.
      recaptchaToken: this.turnstileToken ?? '',

      // DSGVO Consent (beweissicher im Backend gespeichert)
      TermsAccepted: formValues.termsAccepted || false,
      PrivacyPolicyAccepted: formValues.privacyPolicyAccepted || false,
      ConsentVersion: GDPR_CONSTANTS.CURRENT_CONSENT_VERSION,
      UserAgent: navigator.userAgent,
      JourneyId: this.funnelContext['journeyId'] || '',
      // IP-Adresse wird server-seitig aus dem HttpContext ermittelt
    };
    // onError: Turnstile-Widget neu laden, damit ein Folge-Versuch einen frischen
    // Einmal-Token bekommt (sonst 'timeout-or-duplicate' beim erneuten Absenden).
    this.authService.register(
      user,
      (error) => {
        this.trackFunnelStep('submit_failed', {
          Attempt: this.submitAttemptNumber,
          HttpStatus: error?.status || 0,
          ErrorCode: this.registrationErrorCode(error)
        }, false);
        this.resetTurnstile();
      },
      (response) => {
        // Seit BE-12 P5 heißt Register-200 nur „Anfrage angenommen“ (gleiche Antwort für neue und
        // bekannte Adressen). Die Funnel-Stufe danach richtet sich nach dem Login-Ergebnis.
        this.trackFunnelStep('registration_submitted', { Attempt: this.submitAttemptNumber }, false);
        // Conversion beim Absenden; transaction_id = RegistrationId (CompanyId ist immer 0).
        this.googleAdsConversion.trackRegistrationCompleted(response);
      },
      (loggedIn, outcome) => {
        if (loggedIn) {
          // Bekanntes Konto (richtiges Passwort zu registrierter Adresse) ist keine Neuanmeldung.
          this.trackFunnelStep(
            outcome?.existingAccount ? 'registration_existing_signed_in' : 'registration_succeeded',
            { Attempt: this.submitAttemptNumber },
            false
          );
          return;
        }
        this.trackFunnelStep('registration_check_inbox', { Attempt: this.submitAttemptNumber }, false);
        this.checkInboxEmail = user.Email;
      }
    );
  }

  private trackFunnelStep(
    stage: string,
    details: Record<string, string | number | boolean> = {},
    once = true
  ): void {
    if (once && this.emittedFunnelStages.has(stage)) return;
    this.emittedFunnelStages.add(stage);
    this.logger.logInfo('RegistrationFunnelStep', LogCategory.Business, {
      JourneyId: this.funnelContext['journeyId'] || '',
      TrackingId: this.funnelContext['trackingId'] || '',
      FunnelStage: stage,
      Ref: this.funnelContext['ref'] || '',
      CtaLocation: this.funnelContext['cta'] || '',
      LandingPage: this.funnelContext['pg'] || '',
      Device: this.funnelContext['dev'] || '',
      Gclid: this.funnelContext['gclid'] || '',
      // Vollzugriff-Testphase (anonymer Kontext, keine Formulardaten).
      TrialMode: 'full_access',
      PreferredPlanId: this.funnelContext['preferredPlanId'] || '',
      ...details
    });
    this.logger.forceFlush();
  }

  private registrationErrorCode(error: HttpErrorResponse | null | undefined): string {
    if (!error) return 'empty_response';
    const notification = error.error?.notifications?.[0];
    return notification?.code || error.error?.code || `http_${error.status || 0}`;
  }

  /**
   * Macht aus einem Tracking-Parameter garantiert einen einzelnen String.
   *
   * Angular gibt `queryParams['x']` als `string | string[]` zurück — als Array genau dann, wenn
   * der Parameter MEHRFACH in der URL steht. Ungeprüft weitergereicht landet dieses Array im
   * Log und von dort in Auswertungen, die einen Wert erwarten: Am 2026-08-20/21 wies Google
   * zwei Conversions ab, weil die Klick-ID als `["CjwK…","CjwK…"]` gemeldet wurde.
   *
   * Bei mehreren Werten gewinnt der ERSTE: Er stammt aus der ursprünglichen Ziel-URL, spätere
   * Wiederholungen entstehen durch nachträgliches Anhängen. Sind sie identisch — der real
   * beobachtete Fall —, ist die Wahl ohnehin folgenlos.
   */
  private singleParam(raw: unknown): string {
    if (Array.isArray(raw)) {
      const first = raw.find((v) => typeof v === 'string' && v.trim() !== '');
      return typeof first === 'string' ? first.trim() : '';
    }
    return typeof raw === 'string' ? raw.trim() : '';
  }

  private createJourneyId(): string {
    if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0,8)}-${hex.slice(8,12)}-${hex.slice(12,16)}-${hex.slice(16,20)}-${hex.slice(20)}`;
  }

  /** Setzt das Turnstile-Widget zurueck und verwirft den verbrauchten Token. */
  private resetTurnstile(): void {
    const turnstile = (window as unknown as { turnstile?: TurnstileApi })
      .turnstile;
    if (turnstile && this.turnstileWidgetId !== null) {
      turnstile.reset(this.turnstileWidgetId);
    }
    this.turnstileToken = null;
  }

  removeSpaces(controlName: string): void {
    const control = this.registerForm.get(controlName);
    if (control && control.value) {
      const trimmedValue = control.value.trim();
      // Nur setValue aufrufen wenn sich der Wert wirklich ändert
      if (trimmedValue !== control.value) {
        control.setValue(trimmedValue);
      }
    }
  }
}
